import type { ApiChat, ApiMessage, ApiUser } from '../api/types';
import type { GlobalState } from '../global/types';
import { getMessageText } from '../global/helpers';
import { getUserFullName, getMainUsername } from '../global/helpers/users';
import { isUserId } from '../util/entities/ids';

import { extractEntitiesFromMessageEntities } from './entityExtractor';
import {
  linkEntities, loadForensicDb, setForensicConfig, upsertMessage, upsertPeer,
} from './store';
import type { ForensicMessage } from './types';

function ingestPeerFromApi(chat?: ApiChat, user?: ApiUser) {
  if (user) {
    upsertPeer({
      id: user.id,
      type: 'user',
      title: getUserFullName(user) || user.id,
      username: getMainUsername(user),
      phone: user.phoneNumber,
      photoId: user.avatarPhotoId,
      bio: undefined,
      updatedAt: Date.now(),
    });
    return;
  }
  if (!chat) return;

  const type = chat.type === 'chatTypeChannel' ? 'channel'
    : (chat.type === 'chatTypePrivate' ? 'user' : 'chat');

  upsertPeer({
    id: chat.id,
    type,
    title: chat.title || chat.id,
    username: getMainUsername(chat),
    updatedAt: Date.now(),
  });
}

export async function ingestFromGlobal(global: GlobalState, chatIds?: string[]) {
  await loadForensicDb();

  const targetChatIds = chatIds?.length
    ? chatIds
    : Object.keys(global.messages.byChatId);

  targetChatIds.forEach((chatId) => {
    const chat = global.chats.byId[chatId];
    const user = isUserId(chatId) ? global.users.byId[chatId] : undefined;
    ingestPeerFromApi(chat, user);

    const fullInfo = global.chats.fullInfoById[chatId] || global.users.fullInfoById[chatId];
    if (fullInfo && 'about' in fullInfo && fullInfo.about) {
      upsertPeer({
        id: chatId,
        type: isUserId(chatId) ? 'user' : (chat?.type === 'chatTypeChannel' ? 'channel' : 'chat'),
        title: chat?.title || (user ? getUserFullName(user) : chatId),
        username: chat ? getMainUsername(chat) : user ? getMainUsername(user) : undefined,
        phone: user?.phoneNumber,
        bio: fullInfo.about,
        updatedAt: Date.now(),
      });
    }

    const chatMessages = global.messages.byChatId[chatId];
    if (!chatMessages?.byId) return;

    const ingestMessage = (message: ApiMessage | undefined) => {
      if (!message || typeof message.id !== 'number' || message.isTypingDraft) return;

      const textContent = getMessageText(message);
      const text = textContent?.text || '';

      const forensicMsg: ForensicMessage = {
        id: `${chatId}_${message.id}`,
        chatId,
        messageId: message.id,
        senderId: message.senderId,
        date: message.date,
        text,
        isDeleted: message.isDeleted,
        isEdited: message.isEdited,
        ingestedAt: Date.now(),
      };
      upsertMessage(forensicMsg);

      if (text) {
        const entities = extractEntitiesFromMessageEntities(text, textContent?.entities);
        linkEntities(entities, chatId, message.id, text.slice(0, 120));
      }
    };

    Object.values(chatMessages.byId).forEach(ingestMessage);

    const scheduledById = global.scheduledMessages?.byChatId[chatId]?.byId;
    if (scheduledById) {
      Object.values(scheduledById).forEach(ingestMessage);
    }
  });

  setForensicConfig('exportedAt', String(Date.now()));
  setForensicConfig('currentUserId', global.currentUserId || '');
  setForensicConfig('appVersion', 'telegram-tt-rasi');
}